import { useMemo } from 'react';
import { type InferParamsType, type QueryParamConfigMap, useUrlQuerySync } from './useUrlQuerySync';

/**
 * Generic base hook for query synchronization with URL
 * Eliminates code duplication across different entity query sync hooks
 */
export function useBaseQuerySync<TConfig extends QueryParamConfigMap, TParams extends InferParamsType<TConfig>, TApiParams>({
    config,
    mapToApiParams,
    debounceMs = 300,
}: {
    config: TConfig;
    mapToApiParams: (params: TParams) => TApiParams;
    debounceMs?: number;
}) {
    const urlSync = useUrlQuerySync<TParams>({
        config,
        debounceMs,
    });

    // Transform params for API consumption with memoization
    const apiParams = useMemo(() => {
        return mapToApiParams(urlSync.params);
    }, [urlSync.params, mapToApiParams]);

    // Function to apply filters - handles complete filter replacement
    const applyFilters = (filters: any) => {
        // Start with current params to preserve all existing values (including defaults)
        const currentParams: any = { ...urlSync.params };

        // Reset to page 1 when filters change (but keep other params)
        currentParams.page = 1;

        // First, remove any existing filters that are not in the new filters object
        // (This handles the case where filters are removed by omitting them)
        Object.keys(currentParams).forEach(key => {
            // Only skip core pagination params - all filters should be removable by users
            if (key !== 'page' && key !== 'size' && !(key in filters)) {
                // Remove any filter that's not in the new filters (user wants it removed)
                delete currentParams[key];
            }
        });

        // Apply ALL filters from the input (this should be the complete filter set)
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        currentParams[key] = value;
                    } else {
                        currentParams[key] = [];
                    }
                } else {
                    currentParams[key] = value;
                }
            } else {
                delete currentParams[key];
            }
        });

        // Update URL params with immediate application - use replace to avoid history spam
        // Use a function to completely replace the params instead of merging
        urlSync.updateParams(() => currentParams as any, { immediate: true, replace: true });
    };

    return {
        // URL-synchronized parameters
        params: urlSync.params,
        apiParams,

        // Update functions
        updateParams: urlSync.updateParams,
        resetParams: urlSync.resetParams,
        setParam: urlSync.setParam,
        getParam: urlSync.getParam,

        // Filter application
        applyFilters,

        // Convenience functions for common operations
        setPage: (page: number) => urlSync.setParam('page', page, { immediate: true }),
        setPageSize: (size: number) => {
            urlSync.setParam('size', size, { immediate: true });
            urlSync.setParam('page', 1, { immediate: true });
        },

        // Reset with immediate URL update
        reset: () => urlSync.resetParams(false),
    };
}
